<template>
  <Hero :title="data?.header.title" :subtitle="data?.header.subtitle" :narrow="true" :images="data?.header.images"/>
  <main class="main maxw--waist block">
    <form @submit.prevent="submit" class="form">
        <h2>{{ data?.intro_heading }}</h2>
        <div v-html="$md.render(data?.intro_text || '')" class="rendered"></div>

      <fieldset name="general" class="block form__fieldset">
        <h3 class="text--default">Algemene gegevens</h3>
        <div class="form__row">
          <FormTextField label="Voornaam" name="firstname" class="field--w1-2" v-model="v$.firstname.$model" :validator="v$.firstname"/>
          <FormTextField label="Achternaam" name="lastname" class="field--w1-2" v-model="v$.lastname.$model" :validator="v$.lastname"/>
        </div>
        <div class="form__row">
          <FormTextField label="Email" name="email" type="email" v-model="v$.email.$model" :validator="v$.email"/>
        </div>
        <div class="form__row">
          <FormTextField label="Telefoonnummer" name="phone" v-model="v$.phone.$model" :validator="v$.phone"/>
        </div>
        <div class="form__row">
          <FormTextField label="Adres" name="address" v-model="v$.address.$model" :validator="v$.address"/>
        </div>
        <div class="form__row">
          <FormTextField label="Postcode" name="postalcode" class="field--w1-3" v-model="v$.postalcode.$model" :validator="v$.postalcode"/>
          <FormTextField label="Woonplaats" name="city" class="field--w2-3" v-model="v$.city.$model" :validator="v$.city"/>
        </div>
        <div class="form__row">
          <FormTextField label="Geboortedatum" name="birthdate" v-model="v$.birthdate.$model" :validator="v$.birthdate"/>
        </div>
        <div class="form__row">
          <FormTextField label="Opleiding" name="study" v-model="v$.study.$model" :validator="v$.study"/>
        </div>
      </fieldset>
      <fieldset name="payment" class="block form__fieldset">
        <h3 class="text--default">Betalingsgegevens</h3>
        <div class="form__row">
          <FormTextField label="IBAN" name="iban" v-model="v$.iban.$model" :validator="v$.iban"/>
        </div>
        <div class="form__row">
          <FormTextField label="Rekeninghouder" name="bankholder" v-model="v$.bankholder.$model" :validator="v$.bankholder"/>
        </div>
      </fieldset>
      <fieldset name="terms" class="block form__fieldset">
        <div v-html="$md.render(data?.confirmation_content || '')" class="rendered"></div>
        <FormCheckbox v-model="v$.consent.$model" name="consent" label="Ik ga met al het bovenstaande akkoord" :validator="v$.consent"/>
        <NuxtTurnstile v-model="turnstileToken" />
      </fieldset>
      <fieldset name="submit" class="form__fieldset">
        <div class="form__row">
          <button class="btn btn--flex" type="submit" :disabled="isLoading || !v$.$dirty">
            Verzenden
            <Spinner v-if="isLoading"/>
          </button>
        </div>
        <div class="form__row">
          <span 
            class="mt-1 field__status"
            :class="{ 'success': sendStatus > 0, 'failed': sendStatus < 0 }"
            v-if="sendStatus !== 0"
          >
            {{ feedbackMessage }}
          </span>
        </div>
      </fieldset>
    </form>
  </main>
</template>

<script lang="ts" setup>
import useVuelidate from '@vuelidate/core';
import { email, helpers, required, sameAs } from '@vuelidate/validators';
import EnrolmentPage from '~/typings/pages/enrolment_page';
import Enrolment from '~/typings/items/enrolment';
import * as ibantools from 'ibantools';

useHead({
  title: 'Inschrijven'
});

const { findOne, create } = useStrapi();

const { data } = await useAsyncData(
  'enrolmentpage',
  () => findOne<EnrolmentPage>('enrolment-page', { populate: ['header', 'header.images'] }),
  {
    transform: flattenStrapiObject
  }
);

const baseData = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  address: '',
  postalcode: '',
  city: '',
  birthdate: '',
  study: '',
  iban: '',
  bankholder: '',
  consent: false
}

const formData = reactive<Enrolment>({ ...baseData });

const turnstileToken = ref('');
const sendStatus = ref<number>(0);
const { isLoading, startLoading, stopLoading } = useLoading();

const feedbackMessage = computed<string>(() => {
  if (sendStatus.value > 0) {
    return 'Email is verzonden.';
  } else if (sendStatus.value < 0) {
    return 'Er is iets fout gegaan bij het versturen van het formulier.'
  } else {
    return '';
  }
});

const submit = async (e: Event) => {
  sendStatus.value = 0;

  if (isLoading.value) return;

  startLoading();

  if (!(await v$.value.$validate())) {
    stopLoading();
    return;
  }

  try {
    const result = await $fetch('/_turnstile/validate', {
      method: 'POST',
      body: { token: turnstileToken.value }
    });

    if (!result || !result.success) {
      sendStatus.value = -1;
      return;
    }

    await create<Enrolment>('enrolments', formData);

    sendStatus.value = 1;
    Object.assign(formData, baseData);
    v$.value.$reset();

    setTimeout(() => sendStatus.value = 0, 3000);

  } catch (e) {
    sendStatus.value = -1;
  } finally {
    stopLoading();
  }
}

const rules = computed(() => ({
  firstname: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  lastname: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  email: {
    required: helpers.withMessage('Dit veld is verplicht.', required),
    email: helpers.withMessage('Je dient een geldig emailadres in te vullen.', email)
  },
  phone: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  address: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  postalcode: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  city: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  birthdate: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  study: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  iban: {
    required: helpers.withMessage('Dit veld is verplicht.', required),
    iban: helpers.withMessage('Je dient een geldig IBAN nummer in te vullen.', (value: any): boolean => ibantools.isValidIBAN(value))
  },
  bankholder: {
    required: helpers.withMessage('Dit veld is verplicht.', required)
  },
  consent: {
    required: helpers.withMessage('Je dient akkoord te gaan met de voorwaarden.', sameAs(true))
  }
}));

const v$ = useVuelidate(rules, formData);
</script>
